<template>
  <div class="wrapper">
    <h2 class="page_title">{{ $t("page.api.mainheader") }}</h2>

    <div class="intro">
      <p class="intro__text">{{ $t(`page.api.lead${textSuffix}`) }}</p>

      <i18n-t class="intro__text --bold" keypath="page.api.channel" tag="p">
        <template #method>
          <code class="intro__code">{{ CHANNEL_METHOD }}</code>
        </template>
      </i18n-t>

      <i18n-t class="intro__text" :keypath="`page.api.hooks_lead${textSuffix}`" tag="p">
        <template #webhook><b>webhook</b></template>
        <template #incoming><b>INCOMING / OUTGOING</b></template>
        <template #status><b>STATUS</b></template>
        <template #voip><b>VOIP EVENTS</b></template>
      </i18n-t>

      <div class="intro__hooks">
        <div v-for="hook in hooks" :key="hook.key" class="hook">
          <p class="hook__title">
            <i :class="hook.icon"></i>
            {{ $t(`page.api.hooks.${hook.key}_title`) }}
          </p>
          <p class="hook__text">{{ $t(`page.api.hooks.${hook.textKey ?? hook.key}_text`) }}</p>
        </div>
      </div>
    </div>

    <div class="cards">
      <div
        v-for="service in services"
        :key="service.key"
        class="api-card"
        :style="{ '--svc': service.color, background: service.tint }"
      >
        <div class="api-card__head">
          <span class="api-card__icon" :style="{ background: service.color }">
            <img :src="service.svg" :alt="service.title" />
          </span>
          <p class="api-card__name">{{ service.title }} API</p>
        </div>

        <ul class="api-card__features">
          <li v-for="(feature, i) in $tm(`page.api.features.${service.key}`)" :key="i">
            <svg class="api-card__check" viewBox="0 0 19 19" fill="none">
              <circle cx="9.5" cy="9.5" r="9.5" fill="#ffffff" />
              <path d="M6 10.5L8 12.5L13.5 7" :stroke="service.color" stroke-width="2" />
            </svg>
            <span>{{ feature }}</span>
          </li>
        </ul>

        <a class="api-card__button" :href="service.url" target="_blank" rel="noopener">
          <i class="bi bi-box-arrow-up-right"></i>
          {{ $t("page.api.button") }}
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
// core
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

// data
import MESSENGERS, { TIKTOK } from "@/data/messengers.js";

// assets
import viberTelephonySvg from "@/assets/icons/messengers/viber_telephony.svg";

const CHANNEL_METHOD = "/channel/connect";

// 7% of the service colour over white; a literal value keeps the page off
// color-mix(), which the project does not use anywhere else
const TINTS = {
  viber: "#f6f3f8",
  telegram: "#f0f9fd",
  whatsapp: "#f0fcf4",
  tiktok: "#ededed",
  telephony: "#f6f5fd",
};

const DOCS = {
  viber: "https://e-chat.tech/api/viber/v2/documentation",
  telegram: "https://telegram.e-chat.tech/api/documentation",
  whatsapp: "https://e-chat.tech/api/whatsapp/v1/documentation",
  tiktok: "https://e-chat.tech/api/tiktok/v1/documentation",
  telephony: "https://e-chat.tech/api/voip/v1/documentation",
};

const { t: $t } = useI18n();
const store = useStore();

const HOOKS = [
  { key: "incoming", icon: "bi bi-arrow-down-left" },
  { key: "status", icon: "bi bi-check2-circle" },
  { key: "voip", icon: "bi bi-telephone-outbound" },
];

// telephony and TikTok are open to the early access accounts only: the rest
// get the texts about numbers alone, without the voip webhook and their cards
const hasEarlyAccess = computed(() => store.getters["auth/hasEarlyAccess"]);
const textSuffix = computed(() => (hasEarlyAccess.value ? "" : "_numbers"));

const hooks = computed(() => {
  if (hasEarlyAccess.value) return HOOKS;

  return HOOKS.filter((hook) => hook.key !== "voip").map((hook) =>
    hook.key === "incoming" ? { ...hook, textKey: "incoming_numbers" } : hook
  );
});

const services = computed(() => [
  ...Object.values(MESSENGERS).map((msgr) => ({
    key: msgr.value,
    title: msgr.title,
    color: msgr.color,
    tint: TINTS[msgr.value],
    svg: msgr.svg,
    url: DOCS[msgr.value],
  })),
  {
    key: TIKTOK.value,
    title: TIKTOK.title,
    color: TIKTOK.color,
    tint: TINTS.tiktok,
    svg: TIKTOK.svg,
    url: DOCS.tiktok,
  },
  {
    key: "telephony",
    title: $t("page.api.telephony_title"),
    color: "var(--general-color)",
    tint: TINTS.telephony,
    svg: viberTelephonySvg,
    url: DOCS.telephony,
  },
].filter((service) => hasEarlyAccess.value || ![TIKTOK.value, "telephony"].includes(service.key)));
</script>

<style lang="scss" scoped>
@use "@/styles/pages/instruction.scss";

.intro {
  margin-bottom: 30px;
  padding: 24px 30px;
  border-radius: 11px;
  background: var(--block-color);
  box-sizing: border-box;

  &__text {
    margin: 0 0 12px;
    font-family: "Gilroy", sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 25px;
    color: var(--text-third-color);

    b {
      font-weight: 700;
      color: var(--text-color);
    }

    &.--bold {
      font-weight: 700;
      color: var(--text-color);
    }
  }

  &__code {
    padding: 2px 8px;
    border-radius: 5px;
    border: 1px solid #cdcaf3;
    background: #ffffff;
    font-family: "Menlo", "Consolas", monospace;
    font-size: 14px;
    color: var(--general-color);
  }

  &__hooks {
    display: flex;
    gap: 16px;
    margin-top: 16px;

    @media screen and (max-width: 900px) {
      flex-direction: column;
    }
  }
}

.hook {
  flex: 1;
  padding: 14px 16px;
  border: 1px solid #cdcaf3;
  border-radius: 7px;
  background: #ffffff;
  box-sizing: border-box;

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 6px;

    font-family: "Gilroy", sans-serif;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--text-color);

    i {
      font-size: 15px;
      color: var(--general-color);
    }
  }

  &__text {
    margin: 0;
    font-family: "Gilroy", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: var(--text-third-color);
  }
}

.cards {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
}

.api-card {
  flex: 0 0 calc(33.333% - 20px);
  box-sizing: border-box;

  display: flex;
  flex-direction: column;

  padding: 30px;
  border-radius: 11px;

  &__head {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 16px;
  }

  &__icon {
    width: 55px;
    height: 55px;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    border-radius: 11px;

    img {
      width: 44px;
      height: 44px;
    }
  }

  &__name {
    margin: 0;
    font-family: "Gilroy", sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: var(--text-color);
  }

  &__features {
    list-style: none;
    margin: 0 0 20px;
    padding: 0;

    display: flex;
    flex-direction: column;
    gap: 8px;

    li {
      display: flex;
      align-items: flex-start;
      gap: 10px;

      font-family: "Gilroy", sans-serif;
      font-size: 15px;
      font-weight: 500;
      line-height: 22px;
      color: var(--text-third-color);
    }
  }

  &__check {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    margin-top: 2px;
  }

  // .btn-icon skin of BaseButton
  &__button {
    align-self: center;
    margin-top: auto;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;

    height: 40px;
    padding: 0 16px;

    border: 1px solid var(--svc);
    border-radius: 9px;
    background: #ffffff;
    color: var(--svc) !important;
    text-decoration: none;
    cursor: pointer;
    transition: 0.25s;

    font-family: "Gilroy", sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 17px;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    white-space: nowrap;

    i {
      font-size: 16px;
    }

    &:hover {
      background: var(--svc);
      color: #ffffff !important;
    }
  }

  @media screen and (max-width: 1100px) {
    flex: 0 0 calc(50% - 15px);
  }

  @media screen and (max-width: 700px) {
    flex: 0 0 100%;
  }
}
</style>
